import { Component, Input, OnInit } from '@angular/core';
import { map, Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';
import type { Entity } from '../../../core/models/entity.model';
import type { CombinedPropertyDetails } from '../../store/product-view/helpers/multi-edit-product.helpers';
import { Store } from '@ngrx/store';
import { createMultiEditPropertyEditorForProperty } from '../../store/product-view/product-view.selectors';
import { selectCurrentProjectEntities } from '../../store/project-data/project-data.selectors';
import type { EntityType } from '../../../core/models/entity-type.model';
import { selectEntityTypes } from '../../../core/store/entity-types/entity-types.selectors';
import { TranslateService } from '@ngx-translate/core';
import type { MeasurementUnit } from '../../../core/models/measurement-unit.model';
import { selectMeasurementUnits } from '../../../core/store/measurement-units/measurement-units.selector';
import { createMultiEditPropertyToolTipObjectTree, ToolTipHelperObject } from './multi-edit-property-tooltip-content.helpers';

@Component({
  selector: 'app-multi-edit-property-tooltip-content',
  templateUrl: './multi-edit-property-tooltip-content.component.html',
  styleUrls: ['./multi-edit-property-tooltip-content.component.scss'],
  standalone: false
})
export class MultiEditPropertyTooltipContentComponent implements OnInit {
  @Input() propertyKey: string;

  protected propertyDetail$: Observable<CombinedPropertyDetails>;
  protected projectEntities$: Observable<Entity[]>;
  protected entityTypes$: Observable<Record<string, EntityType>>;
  protected measurementUnits$: Observable<Record<string, MeasurementUnit>>;
  protected toolTips$: Observable<ToolTipHelperObject[]>;

  constructor(private store: Store, private translateService: TranslateService) {}

  ngOnInit(): void {
    this.propertyDetail$ = this.store.select(createMultiEditPropertyEditorForProperty(this.propertyKey));
    this.projectEntities$ = this.store.select(selectCurrentProjectEntities);
    this.entityTypes$ = this.store.select(selectEntityTypes);
    this.measurementUnits$ = this.store.select(selectMeasurementUnits);
    this.toolTips$ = this.createCombinedToolTipObservable();
  }

  private createCombinedToolTipObservable(): Observable<ToolTipHelperObject[]> {
    return this.propertyDetail$.pipe(
      withLatestFrom(this.projectEntities$, this.entityTypes$, this.measurementUnits$),
      map(([detail, entities, types, measurementUnits]) => {
        if (!detail.valuesDiffer) {
          return [];
        } else {
          const nameResolver = (e: Entity) => this.getName(e, types);
          return createMultiEditPropertyToolTipObjectTree(detail, entities, measurementUnits, nameResolver);
        }
      })
    );
  }

  private getName(entity: Entity, entityTypes: Record<string, EntityType>): string {
    const nameFromProperty = entity.Properties['Name']?.Value as string;
    if (nameFromProperty) {
      return nameFromProperty;
    }

    return this.translateService.instant(entityTypes[entity.EntityTypeKey]?.NameId);
  }
}
